import { useEffect, useRef } from "react";
import { useDict } from "@/i18n/useDict";

// Brand-name proper nouns — same across every language, so they live here, not
// in the translation dictionary.
const PLATFORMS = [
  { name: "Facebook", short: "f", color: "#1877F2" },
  { name: "Instagram", short: "Ig", color: "#E1306C" },
  { name: "LinkedIn", short: "in", color: "#0A66C2" },
  { name: "TikTok", short: "Tk", color: "#00C2B8" },
  { name: "Google Business", short: "G", color: "#34A853" },
];

const MarketingSection = () => {
  const { dict } = useDict();
  const m = dict.marketing;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const featureIcons = ["🔗", "📅", "📊", "🤖"];
  const features = m.features.map((f, i) => ({ ...f, icon: featureIcons[i] }));

  return (
    <section id="marketing" className="py-20 md:py-32 px-6 relative overflow-hidden scroll-mt-20">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-pink-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: text content */}
          <div className="animate-on-scroll">
            <div className="mb-6">
              <span
                className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full"
                style={{
                  background: "linear-gradient(135deg, rgba(236,72,153,0.12) 0%, rgba(244,114,182,0.12) 100%)",
                  color: "#db2777",
                  border: "1px solid rgba(236,72,153,0.25)",
                }}
              >
                {m.badge}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground leading-tight mb-4">
              {m.titleLead}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-orange-500">
                {m.titleHighlight}
              </span>
              {m.titleTrail}
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              {m.introPre}<strong className="text-foreground">{m.introStrong}</strong>{m.introPost}
            </p>

            {/* Uniqueness callout */}
            <div
              className="flex items-center gap-2 rounded-xl px-4 py-3 mb-8 text-sm font-medium"
              style={{
                background: "linear-gradient(135deg, rgba(236,72,153,0.08) 0%, rgba(251,146,60,0.08) 100%)",
                border: "1px solid rgba(236,72,153,0.2)",
                color: "#be185d",
              }}
            >
              <span className="text-base flex-shrink-0">✨</span>
              <span>{m.unique}</span>
            </div>

            {/* Features list */}
            <div className="space-y-4">
              {features.map((feature) => (
                <div key={feature.title} className="flex gap-4">
                  <div className="text-2xl flex-shrink-0">{feature.icon}</div>
                  <div>
                    <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="#preturi"
              className="inline-flex items-center justify-center rounded-xl px-8 py-3 mt-8 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{
                background: "linear-gradient(135deg, #ec4899 0%, #f97316 100%)",
                boxShadow: "0 8px 24px rgba(236, 72, 153, 0.3)",
              }}
            >
              {m.cta}
            </a>
          </div>

          {/* Right: social media dashboard preview */}
          <div className="animate-on-scroll">
            <div
              className="rounded-2xl p-6 relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #2d0a24 0%, #4a1340 50%, #7c2d5e 100%)",
                boxShadow: "0 20px 60px rgba(236, 72, 153, 0.22)",
              }}
            >
              {/* shimmer accent */}
              <div
                className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full blur-3xl opacity-40"
                style={{ background: "radial-gradient(circle, #f472b6 0%, transparent 70%)" }}
              />

              {/* Header */}
              <div className="relative flex items-center gap-3 mb-5 pb-4 border-b border-white/10">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(244, 114, 182, 0.2)" }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f9a8d4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                </div>
                <div className="text-[10px] font-bold tracking-widest uppercase text-pink-200/80">
                  {m.previewTag}
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-400"></span>
                  </span>
                  <span className="text-[10px] font-bold tracking-widest uppercase text-pink-200/80">
                    {m.connectedLabel}
                  </span>
                </div>
              </div>

              {/* Connected platform chips */}
              <div className="relative flex flex-wrap gap-2 mb-5">
                {PLATFORMS.map((p) => (
                  <div
                    key={p.name}
                    className="flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1"
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                      style={{ background: p.color }}
                    >
                      {p.short}
                    </span>
                    <span className="text-xs font-medium text-white/85">{p.name}</span>
                  </div>
                ))}
              </div>

              {/* Composer card */}
              <div
                className="relative rounded-xl p-4 mb-5"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <p className="text-sm text-white/50 mb-4">{m.composerPlaceholder}</p>
                <div className="flex items-center justify-between">
                  <div className="flex -space-x-1.5">
                    {PLATFORMS.map((p) => (
                      <span
                        key={p.name}
                        className="w-5 h-5 rounded-full border"
                        style={{ background: p.color, borderColor: "#3a1030" }}
                      />
                    ))}
                  </div>
                  <span
                    className="text-xs font-semibold text-white rounded-lg px-3 py-1.5"
                    style={{ background: "linear-gradient(135deg, #ec4899 0%, #f97316 100%)" }}
                  >
                    {m.postButton}
                  </span>
                </div>
              </div>

              {/* Mini analytics row */}
              <div className="relative grid grid-cols-3 gap-2">
                {m.stats.map((s) => (
                  <div
                    key={s.label}
                    className="rounded-xl px-3 py-3 text-center"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    <div className="text-base font-bold text-pink-200">{s.value}</div>
                    <div className="text-[10px] text-white/55 leading-tight mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketingSection;
